import { useMemo, useState } from 'react';
import { Utility } from '@nkr/services/utility';
import { AgGridReact } from 'ag-grid-react';
import { GridWrapper } from './styles';
import { ColDef, FirstDataRenderedEvent, GridReadyEvent, ICellRendererParams, IHeaderParams } from 'ag-grid-community';
import { IVoucherHolderForList } from '@nkr/models/recipients';
import { VoucherHolderType } from '@nkr/enums/index';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import ActionButtonRenderer, { VoucherHolderAction } from './action-button-renderer';
import FileIcon from './file-icon';

interface IProps {
    data: IVoucherHolderForList[],
    isAdmin: boolean,
    isAdminLogin: boolean,
    donorHasDonated: boolean,
    onAction: (voucherHolderId: number, type: VoucherHolderAction) => void,
    onDownload: (voucherHolderId: number, docType: number) => void
}

const VoucherHoldersGrid = (props: IProps) => {
    const [menuDisplayed, setMenuDisplayed] = useState(false);

    const handleMenuToggle = (show: boolean) => {
        setMenuDisplayed(show);
    };

    const handleItemSelected = (id: number, action: VoucherHolderAction) => {
        props.onAction(id, action);
    };

    const cols: ColDef[] = [
        {
            colId: 'dateAdded',
            field: 'dateAdded',
            width: 120,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Date Added</SortableHeaderComponent>
        },
        {
            colId: 'firstName',
            field: 'firstName',
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>First Name</SortableHeaderComponent>
        },
        {
            colId: 'lastName',
            field: 'lastName',
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Last Name</SortableHeaderComponent>
        },
        {
            colId: 'birthDate',
            field: 'birthDate',
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Date of Birth</SortableHeaderComponent>,
            comparator: Utility.dateComparator
        },
        {
            colId: 'files',
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Files</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IVoucherHolderForList;

                return (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <FileIcon
                            voucherHolderId={data.id}
                            docType={0}
                            fileName="Voucher Consent File"
                            value={data.adpInformedConsentFile}
                            inProcess={false}
                            onDownload={props.onDownload}
                        />

                        {data.typeId != VoucherHolderType.StandardVoucher && (
                            <FileIcon
                                voucherHolderId={data.id}
                                docType={1}
                                fileName="HLA Swab Results File"
                                value={data.hlaSwabResultsFile}
                                inProcess={data.swabsOrderedDateTime != ''}
                                onDownload={props.onDownload}
                            />
                        )}

                        {data.typeId != VoucherHolderType.StandardVoucher && (
                            <FileIcon
                                voucherHolderId={data.id}
                                docType={2}
                                fileName="Screening Results File"
                                value={data.screeningResultsFile}
                                inProcess={data.lastScreeningOrderedDateTime != ''}
                                onDownload={props.onDownload}
                            />
                        )}
                    </div>
                )
            },
            sortable: false
        },
        {
            colId: 'type',
            field: 'type',
            width: 120,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Type</SortableHeaderComponent>
        },
        {
            colId: 'status',
            field: 'status',
            width: 120,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Status</SortableHeaderComponent>
        },
        {
            colId: 'recipient',
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient Record</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IVoucherHolderForList;

                if (data.recipientId == 0) {
                    return <>--</>
                } else if (data.donorCenterId == data.recipientCenterId) {
                    return <a href={`center/${data.recipientCenterId}/recipient/${data.recipientId}/profile/general`}>{data.recipientAlias}</a>
                } else {
                    return <>{data.recipientAlias} @ {data.recipientCenterAlias}</>
                }
            }
        },
        {
            field: 'actionId',
            headerName: '',
            width: 70,
            cellClass: 'action-menu',
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IVoucherHolderForList;

                return (
                    <ActionButtonRenderer {...params}
                        data={data}
                        isAdmin={props.isAdmin}
                        isAdminLogin={props.isAdminLogin}
                        donorHasDonated={props.donorHasDonated}
                        onMenuToggle={handleMenuToggle}
                        onItemSelected={handleItemSelected}
                    />
                )
            }
        }
    ];

    const columnDefs = useMemo(() => cols, []);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <GridWrapper className="ag-theme-bootstrap">
            <AgGridReact
                defaultColDef={{
                    headerClass: 'list-header',
                    cellClass: 'list-cell',
                    sortable: true
                }}
                columnDefs={columnDefs}
                rowData={rowData}
                rowHeight={30}
                suppressCellSelection={true}
                suppressRowClickSelection={true}
                suppressContextMenu={true}
                noRowsOverlayComponentFramework={() => <div>No voucher holders...</div>}
                onGridReady={(e: GridReadyEvent) => {
                    e.api.sizeColumnsToFit();
                }}
                onFirstDataRendered={(e: FirstDataRenderedEvent) => {
                    e.api.sizeColumnsToFit();
                }}
            >
            </AgGridReact>
        </GridWrapper>
    );
}

export default VoucherHoldersGrid;